import React, { useEffect, useState } from "react";
import { getCityBySlug } from "../app/utilities/apis";
import ImageWrapper from "./ui/ImageWrapper";
import LocationSearchInput from "./ui/LocationSearchInput";
import { setLocationCookie } from "../app/utilities/locationCookie";
import type { LocationSuggestion } from "@/types/city";

// Re-export for any consumers that import LocationSuggestion from this module
export type { LocationSuggestion } from "@/types/city";

interface LocationSelectModalProps {
    showModal: boolean;
    onClose: () => void;
    onSelectCity: (city: string) => void; // receives "City, State" format
}

const popularCities = [
    { name: "Delhi", slug: "delhi", state: "Delhi" },
    { name: "Chennai", slug: "chennai", state: "Tamil Nadu" },
    { name: "Mumbai", slug: "mumbai", state: "Maharashtra" },
    { name: "Kolkata", slug: "kolkata", state: "West Bengal" },
    { name: "Hyderabad", slug: "hyderabad", state: "Telangana" },
    { name: "Bangalore", slug: "bangalore", state: "Karnataka" },
    { name: "Ahmedabad", slug: "ahmedabad", state: "Gujarat" },
    { name: "Jaipur", slug: "jaipur", state: "Rajasthan" },
    { name: "Lucknow", slug: "lucknow", state: "Uttar Pradesh" },
    { name: "Chandigarh", slug: "chandigarh", state: "Chandigarh" },
    // Add more popular cities with their states if needed
    // Example: { name: "Alwar", slug: "alwar", state: "Rajasthan" },
];

const LocationSelectModal: React.FC<LocationSelectModalProps> = ({
    showModal,
    onClose,
    onSelectCity,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (showModal) {
            setIsVisible(true);
        }
    }, [showModal]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    // Handle selection from the search input dropdown
    // Note: LocationSearchInput already sets the cookie and dispatches "location-updated"
    // before calling onChange, so we only need to handle UI here.
    const handleSearchSelect = (value: string, selectedLocation: LocationSuggestion | null) => {
        setSearch(value);
        if (selectedLocation) {
            onSelectCity(value);
            handleClose();
        }
    };

    const handlePopularCityClick = async (cityName: string, slug: string, knownState?: string) => {
        try {
            const cityData = await getCityBySlug(slug);
                       
            const location: LocationSuggestion = {
                name: cityData?.name || cityName,
                slug: cityData?.slug || slug,
                state: {
                    name: cityData?.state?.name || "",
                    slug: cityData?.state?.slug || "",
                },
                lat: cityData?.geo?.coordinates?.[1] || 0,
                long: cityData?.geo?.coordinates?.[0] || 0,
            };

            // const displayName = `${location.name}, ${location.state.name}`;
            const displayName = `${location.name}`;

            setLocationCookie(location);
            
            // Dispatch location-updated event for URL redirection
            window.dispatchEvent(
                new CustomEvent("location-updated", {
                    detail: {
                        displayString: displayName,
                        location: location,
                    },
                })
            );
            
            onSelectCity(displayName);
            handleClose();
        } catch (err) {
            console.warn(`Failed to fetch city details for ${cityName} (slug: ${slug})`, err);

            // Fallback using known state from popularCities list
            const fallbackLocation: LocationSuggestion = {
                name: cityName,
                slug: slug,
                state: {
                    name: knownState || "",
                    slug: knownState ? knownState.toLowerCase().replace(/\s+/g, "-") : "",
                },
                lat: 0,
                long: 0,
            };

            const displayName = `${fallbackLocation.name}, ${fallbackLocation.state.name}`;

            setLocationCookie(fallbackLocation);
            
            // Dispatch location-updated event for URL redirection
            window.dispatchEvent(
                new CustomEvent("location-updated", {
                    detail: {
                        displayString: displayName,
                        location: fallbackLocation,
                    },
                })
            );
            
            onSelectCity(displayName);
            handleClose();
        }
    };

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center md:items-center z-[100]">
            <div
                className={`bg-white rounded-t-xl rounded-b-0 w-full max-w-2xl relative sm:p-8 p-4 sm:rounded-lg sm:max-w-2xl max-w-[100vw] sm:mt-0 transition-transform duration-300 ease-in-out ${isVisible ? "translate-y-0" : "translate-y-full"
                    } md:translate-y-0`}
            >
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 sm:top-4 sm:right-6 text-gray-500 hover:text-gray-700 text-2xl"
                    aria-label="Close modal"
                >
                    ×
                </button>

                <h2 className="text-xl font-semibold mb-4">Select Your Location</h2>

                <div className="mb-5">
                    <LocationSearchInput
                        value={search}
                        onChange={handleSearchSelect}
                        placeholder="Search for your city..."
                    />
                </div>

                <div className="flex items-center justify-between mb-3">
                    <div className="text-base font-semibold">Popular Cities</div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 md:gap-6 mt-2">
                    {popularCities.map((city) => (
                        <button
                            key={city.slug}
                            className="flex flex-col items-center p-3 rounded-lg border border-gray-100 hover:bg-gray-50 active:bg-gray-100 focus:outline-none transition-colors"
                            onClick={() => handlePopularCityClick(city.name, city.slug, city.state)}
                        >
                            <ImageWrapper
                                src={`${city.name.toLowerCase()}.svg`}
                                alt={`${city.name} icon`}
                                width={48}
                                height={48}
                                cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                            />
                            <span className="mt-2 text-sm font-medium text-gray-800 text-center">
                                {city.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LocationSelectModal;